import type {
  ExecuteReportRequest,
  ExecuteReportResponse,
  FilterOptionsRequest,
  FilterOption,
} from "../types/reports";
import type {
  StoreOpenSheetRequest,
  StoreOpenSheetRow,
} from "../types/custom-reports";
import { getAuthHeaders, handleAuthError } from "./auth-api";
import { formatApiError } from "./error-message";

// ---------------------------------------------------------------------------
// Scoped v2 client — pos-rest-api (VITE_API_V2_URL). The legacy reports API
// (raw client-built SQL via the generic apiClient/VITE_API_URL) is gone:
// the catalog now owns the SQL server-side, keyed by reportId + queryId.
// ---------------------------------------------------------------------------

let baseUrl = "";
const API_PREFIX = "/api/v1";

export function setReportsBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

interface ApiResponse<T> {
  message: string;
  data: T;
}

async function postReports<T>(endpoint: string, body: unknown): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  const result: ApiResponse<T> = await response.json();
  return result.data;
}

/**
 * Execute a catalog report. The stored SQL is fetched server-side from
 * `<shop>.rptqry`; the client only names the report and supplies declared
 * filter values — no SQL, no `replace`/`replaceWith` placeholder plumbing.
 */
export async function executeReport(
  request: ExecuteReportRequest,
): Promise<ExecuteReportResponse> {
  return postReports<ExecuteReportResponse>("/reports/execute", request);
}

/** Fetch the option list for a report's `select` filter. */
export async function fetchFilterOptions(
  request: FilterOptionsRequest,
): Promise<FilterOption[]> {
  const data = await postReports<{ options: FilterOption[] }>(
    "/reports/filter-options",
    request,
  );
  return data.options;
}

/**
 * Store Open Sheet (legacy custom report `ems_temp_5`). Typed v2
 * endpoint — replaces the legacy `/common/getCustomInvoice` call.
 */
export async function getStoreOpenSheet(
  request: StoreOpenSheetRequest,
): Promise<StoreOpenSheetRow[]> {
  const data = await postReports<{ rows: StoreOpenSheetRow[] }>(
    "/reports/store-open-sheet",
    request,
  );
  return data.rows;
}
